// Rule-based functional interpretation — real interpretation, not a glossary.
// Produces a plain-language summary, key findings, asymmetry note, and flags.
// (A richer AI narrative is a Phase-3 enhancement layered on the same inputs.)

import type { Finding } from "./bands";
import type { Scores } from "./scoring";
import type { TemporalSummary } from "./temporal";
import type { ClinicalMetrics } from "./clinical";
import { referenceRange, evaluateNorm } from "./norms";

export interface Interpretation {
  summary: string;
  keyFindings: string[];
  asymmetry: string;
  flags: string[];
  disclaimer: string;
}

const DISCLAIMER =
  "Functional screening only. Not a motion-lab replacement or diagnostic device. " +
  "Sagittal joint angles are estimated from a single camera and shown for ROM context only.";

export function interpret(input: {
  view: "frontal" | "lateral";
  scores: Scores;
  findings: Finding[];
  temporal: TemporalSummary;
  clinical?: ClinicalMetrics;
  heightCm?: number;
}): Interpretation {
  const { scores, findings, temporal, clinical } = input;

  // --- Summary from overall score ---
  const o = scores.overall;
  const quality = o >= 80 ? "good" : o >= 60 ? "fair" : o >= 40 ? "reduced" : "markedly reduced";
  const driver = lowestSubscore(scores);
  const summary =
    `Overall gait is ${quality} (score ${o}/100)` +
    (driver ? `, with ${driver.label.toLowerCase()} the main limiting factor.` : ".") +
    (scores.confidence !== "high" ? ` Capture confidence is ${scores.confidence}; interpret accordingly.` : "");

  // --- Key findings: anything beyond NORMAL/LOW ---
  const keyFindings = findings
    .filter((f) => !["NORMAL", "LOW"].includes(f.band))
    .sort((a, b) => severityRank(b.band) - severityRank(a.band))
    .slice(0, 6)
    .map((f) => `${f.label}: ${f.band.toLowerCase()}${f.side ? ` (${f.side})` : ""}.`);

  // --- Asymmetry note ---
  let asymmetry = "Movement is broadly symmetric between sides.";
  const si = temporal.stanceSymmetryIndex;
  if (si !== null && si >= 8) {
    const longer = temporal.longerStanceSide;
    asymmetry =
      `Notable left–right asymmetry (stance SI ${si.toFixed(0)}%)` +
      (longer ? `, with longer stance on the ${longer === "L" ? "left" : "right"} side` : "") +
      ` — often the side being protected.`;
  } else if (clinical?.stepLengthSymmetryIndex && clinical.stepLengthSymmetryIndex >= 10) {
    asymmetry = `Step-length asymmetry of ${clinical.stepLengthSymmetryIndex.toFixed(0)}% between sides.`;
  }

  // --- Flags ---
  const flags: string[] = [];
  if (clinical) {
    for (const side of ["L", "R"] as const) {
      const v = clinical.gaitVariabilityPct[side];
      if (v !== null) {
        const r = referenceRange("gaitVariabilityPct");
        if (r && evaluateNorm(v, r).position === "above")
          flags.push(`High gait variability (${side}) — possible instability / fall-risk; consider follow-up.`);
      }
    }
    if (clinical.cadenceStepsPerMin !== null) {
      const r = referenceRange("cadenceStepsPerMin");
      if (r && evaluateNorm(clinical.cadenceStepsPerMin, r).position === "below")
        flags.push("Reduced cadence relative to the typical range.");
    }
  }
  if (scores.confidence === "low")
    flags.push("Low capture quality — re-record (full body in frame, good lighting, lateral walk) for reliable results.");

  return { summary, keyFindings, asymmetry, flags, disclaimer: DISCLAIMER };
}

function severityRank(band: string): number {
  switch (band) {
    case "SEVERE DEVIATION":
    case "HIGH":
      return 3;
    case "MODERATE DEVIATION":
    case "MODERATE":
      return 2;
    case "MILD DEVIATION":
      return 1;
    default:
      return 0;
  }
}

function lowestSubscore(s: Scores): { key: string; label: string; value: number } | null {
  const entries: Array<[string, string, number | undefined]> = [
    ["symmetry", "Symmetry", s.symmetry],
    ["pelvicStability", "Pelvic stability", s.pelvicStability],
    ["kneeControl", "Knee control", s.kneeControl],
    ["trunkStability", "Trunk stability", s.trunkStability],
    ["consistency", "Gait consistency", s.consistency],
  ];
  let best: { key: string; label: string; value: number } | null = null;
  for (const [key, label, value] of entries) {
    if (value === undefined) continue;
    if (!best || value < best.value) best = { key, label, value };
  }
  return best && best.value < 75 ? best : null;
}

export { DISCLAIMER };
